/**
 * Admin · Broker property inspections.
 *
 * Full control of the paid field-inspection workflow that grants the Trusted
 * (Verified) / True (Gold) broker badge: review requests, set a fee, confirm
 * payment, schedule an inspector, record the on-site report, or cancel.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchInspections,
  BADGE_LABEL,
  STATUS_META,
  feeToSar,
  type Inspection,
  type InspectionStatus,
} from '../../../services/inspectionService';
import InspectionDetailModal from './InspectionDetailModal';

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'QUOTED', label: 'Quoted' },
  { value: 'PAID', label: 'Paid' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

function Kpi({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="text-xs text-gray-400 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
    </div>
  );
}

export default function AdminInspectionsPage() {
  const [items, setItems] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [badgeType, setBadgeType] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [kpis, setKpis] = useState({ requested: 0, quoted: 0, scheduled: 0, completed: 0 });
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchInspections({ status: status || undefined, badgeType: badgeType || undefined, page, pageSize: 20 });
      setItems(res.data);
      setTotalPages(res.meta.totalPages);
      setTotal(res.meta.total);
    } catch {
      setError('Failed to load inspections. (Is the property_inspections migration applied to this database?)');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [status, badgeType, page]);

  useEffect(() => { load(); }, [load]);

  const loadKpis = useCallback(async () => {
    try {
      const [r, q, s, c] = await Promise.all([
        fetchInspections({ status: 'REQUESTED', pageSize: 1 }),
        fetchInspections({ status: 'QUOTED', pageSize: 1 }),
        fetchInspections({ status: 'SCHEDULED', pageSize: 1 }),
        fetchInspections({ status: 'COMPLETED', pageSize: 1 }),
      ]);
      setKpis({ requested: r.meta.total, quoted: q.meta.total, scheduled: s.meta.total, completed: c.meta.total });
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadKpis(); }, [loadKpis]);

  const refreshAll = () => { load(); loadKpis(); };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/admin" className="text-sm text-gray-500 hover:text-[#003930] inline-flex items-center gap-1.5 mb-3">
            <i className="ri-arrow-left-line" /> Back to admin
          </Link>
          <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">Broker Inspections</h1>
          <p className="text-gray-500 mt-1">Manage the paid on-site inspection that grants the Trusted / True broker badge - quote, payment, scheduling, and the field report.</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Kpi label="New requests" value={kpis.requested} tone="text-blue-600" />
          <Kpi label="Awaiting payment" value={kpis.quoted} tone="text-indigo-600" />
          <Kpi label="Scheduled" value={kpis.scheduled} tone="text-amber-600" />
          <Kpi label="Completed" value={kpis.completed} tone="text-green-600" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-5">
          <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white">
            {STATUS_FILTERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={badgeType} onChange={(e) => { setPage(1); setBadgeType(e.target.value); }} className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white">
            <option value="">All badges</option>
            <option value="VERIFIED_BROKER">Trusted (Verified)</option>
            <option value="GOLD_BROKER">True (Gold)</option>
          </select>
          <button onClick={refreshAll} className="px-3 py-2.5 rounded-xl border border-gray-200 text-sm bg-white hover:bg-gray-50 inline-flex items-center gap-1.5">
            <i className="ri-refresh-line" /> Refresh
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-gray-400"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
          ) : error ? (
            <div className="py-12 px-6 text-center text-red-500 text-sm">{error}</div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-gray-400">No inspections found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                    <th className="px-4 py-3 font-medium">Broker</th>
                    <th className="px-4 py-3 font-medium">Listing</th>
                    <th className="px-4 py-3 font-medium">Requested badge</th>
                    <th className="px-4 py-3 font-medium text-right">Fee</th>
                    <th className="px-4 py-3 font-medium text-center">Status</th>
                    <th className="px-4 py-3 font-medium">Requested</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const meta = STATUS_META[it.status as InspectionStatus];
                    const brokerName = it.agent?.displayName || [it.agent?.user?.firstName, it.agent?.user?.lastName].filter(Boolean).join(' ') || it.agent?.user?.email || '-';
                    return (
                      <tr key={it.id} className="border-b border-gray-50 hover:bg-[var(--pv-color-surface-strong)]">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{brokerName}</div>
                          <div className="text-xs text-gray-400">{it.agent?.user?.email}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{it.property?.titleEn || it.property?.titleAr || it.propertyId}</td>
                        <td className="px-4 py-3 text-gray-700">{BADGE_LABEL[it.requestedBadgeType]}</td>
                        <td className="px-4 py-3 text-right text-gray-700">{it.feeAmount ? `${feeToSar(it.feeAmount).toLocaleString()} SAR` : '-'}</td>
                        <td className="px-4 py-3 text-center"><span className={`inline-block px-2 py-1 rounded-lg text-xs font-medium ${meta.color}`}>{meta.label}</span></td>
                        <td className="px-4 py-3 text-gray-400 text-xs">{new Date(it.createdAt).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => setOpenId(it.id)} className="px-3 py-1.5 rounded-lg bg-[#004D40] text-white text-xs hover:opacity-90">Manage</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50">Prev</button>
            <span className="text-sm text-gray-500">Page {page} of {totalPages} · {total} total</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-3 py-2 rounded-lg border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50">Next</button>
          </div>
        )}
      </main>

      {openId && (
        <InspectionDetailModal
          inspectionId={openId}
          onClose={() => setOpenId(null)}
          onChanged={refreshAll}
        />
      )}
    </div>
  );
}
